import { useState, useRef, useCallback } from 'react'
import type { BulkEntityType } from '@alpha-edge/shared'
import { Modal, btnPrimary, btnSecondary, colors } from './ui'
import { getAuthToken } from '../services/api'

interface BulkImportModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly entityType: BulkEntityType
  readonly entityLabel: string
  readonly onSuccess: () => void
}

type Step = 'upload' | 'preview' | 'committing'

interface PreviewData {
  readonly validRows: number
  readonly invalidRows: ReadonlyArray<{ row: number; errors: readonly string[] }>
  readonly preview?: readonly Record<string, unknown>[]
  readonly willInsert: number
  readonly willUpdate: number
  readonly conflicts: ReadonlyArray<{ row: number; reason: string }>
}

const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '1em',
  height: '1em',
  border: '2px solid currentColor',
  borderTopColor: 'transparent',
  borderRadius: '50%',
  animation: 'bulkSpin 0.7s linear infinite',
  verticalAlign: 'middle',
  marginRight: '0.5rem',
}

const errorBanner: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: '4px',
  background: colors.bgError,
  border: `1px solid ${colors.borderError}`,
  color: colors.danger,
  marginBottom: '1rem',
  fontSize: '0.9375rem',
}

export function BulkImportModal({ isOpen, onClose, entityType, entityLabel, onSuccess }: BulkImportModalProps) {
  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const fileRef = useRef<File | null>(null)

  function resetState() {
    setStep('upload')
    setFileName(null)
    setPreviewData(null)
    setError(null)
    setSubmitting(false)
    fileRef.current = null
  }

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [onClose])

  function returnToUpload() {
    setStep('upload')
    setFileName(null)
    setPreviewData(null)
    setError(null)
    fileRef.current = null
  }

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      fileRef.current = file
      setFileName(file.name)
      setError(null)
      setSubmitting(true)

      try {
        const formData = new FormData()
        formData.append('file', file)
        const token = getAuthToken()
        const res = await fetch(`/api/bulk/${entityType}/preview`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })

        let body: unknown
        try {
          body = await res.json()
        } catch {
          body = null
        }

        if (res.ok) {
          const data = (body as { data: PreviewData }).data
          setPreviewData(data)
          setStep('preview')
        } else if (
          res.status === 400 &&
          body &&
          typeof body === 'object' &&
          'data' in (body as object) &&
          typeof (body as { data?: { validRows?: unknown } }).data?.validRows === 'number'
        ) {
          // 400 with preview-shape (defensive; handles future route variants)
          setPreviewData((body as { data: PreviewData }).data)
          setStep('preview')
        } else {
          const errMsg =
            body && typeof body === 'object' && 'error' in (body as object)
              ? String((body as { error: unknown }).error)
              : `Upload failed (${res.status})`
          setError(errMsg)
        }
      } catch {
        setError('Network error — please check your connection and try again.')
      } finally {
        setSubmitting(false)
        // Reset so same file can be re-selected after returnToUpload
        e.target.value = ''
      }
    },
    [entityType],
  )

  const handleCommit = useCallback(async () => {
    if (!fileRef.current || !previewData) return
    setStep('committing')
    setSubmitting(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', fileRef.current)
      const token = getAuthToken()
      const res = await fetch(`/api/bulk/${entityType}/commit`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (res.ok) {
        onSuccess()
        resetState()
        onClose()
        return
      }

      let body: unknown
      try {
        body = await res.json()
      } catch {
        body = null
      }

      // Race-safety: commit returned 400 with invalid rows — re-render preview
      if (
        res.status === 400 &&
        body &&
        typeof body === 'object' &&
        'data' in (body as object) &&
        typeof (body as { data?: { validRows?: unknown } }).data?.validRows === 'number'
      ) {
        setPreviewData((body as { data: PreviewData }).data)
        setStep('preview')
        return
      }

      const errMsg =
        body && typeof body === 'object' && 'error' in (body as object)
          ? String((body as { error: unknown }).error)
          : `Commit failed (${res.status})`
      setError(errMsg)
      setStep('preview')
    } catch {
      setError('Network error — please check your connection and try again.')
      setStep('preview')
    } finally {
      setSubmitting(false)
    }
  }, [entityType, previewData, onSuccess, onClose])

  const commitDisabled = !previewData || previewData.invalidRows.length > 0 || submitting

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Import ${entityLabel}`} width="600px">
      <style>{`@keyframes bulkSpin { to { transform: rotate(360deg); } }`}</style>

      {step === 'upload' && (
        <div>
          {error && <div style={errorBanner}>{error}</div>}
          <p style={{ color: colors.text, marginTop: 0 }}>
            Upload a CSV file to import {entityLabel.toLowerCase()}. The server will validate the data and show a
            preview before committing.
          </p>
          <label
            style={{
              display: 'block',
              border: `2px dashed ${colors.border}`,
              borderRadius: '6px',
              padding: '2rem',
              textAlign: 'center',
              cursor: submitting ? 'wait' : 'pointer',
              color: colors.textMuted,
              position: 'relative',
            }}
          >
            {submitting ? (
              <span>
                <span style={spinnerStyle} />
                Parsing…
              </span>
            ) : (
              <span>Click to select a CSV file{fileName ? ` · ${fileName}` : ''}</span>
            )}
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: submitting ? 'wait' : 'pointer' }}
              disabled={submitting}
              onChange={(e) => void handleFileChange(e)}
              data-testid="file-input"
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="button" style={btnSecondary} onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'preview' && previewData && (
        <div>
          {/* Summary card */}
          <div
            style={{
              background: colors.bgMuted,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '1rem',
              marginBottom: '1rem',
              fontSize: '0.9375rem',
              color: colors.text,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{fileName}</div>
            <div>
              {previewData.validRows} valid · {previewData.invalidRows.length} invalid
            </div>
            <div>
              Will insert {previewData.willInsert} · Will update {previewData.willUpdate} · Conflicts{' '}
              {previewData.conflicts.length}
            </div>
          </div>

          {error && <div style={errorBanner}>{error}</div>}

          {previewData.invalidRows.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, color: colors.danger, marginBottom: '0.5rem', fontSize: '0.9375rem' }}>
                Validation errors ({previewData.invalidRows.length} rows)
              </div>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '4px',
                }}
              >
                {previewData.invalidRows.map((r) => (
                  <div
                    key={r.row}
                    style={{
                      borderLeft: `3px solid ${colors.danger}`,
                      padding: '0.5rem 0.75rem',
                      borderBottom: `1px solid ${colors.borderError}`,
                      fontSize: '0.875rem',
                      color: colors.text,
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>Row {r.row}:</span> {r.errors.join('; ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {previewData.conflicts.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, color: '#b45309', marginBottom: '0.5rem', fontSize: '0.9375rem' }}>
                Conflicts ({previewData.conflicts.length})
              </div>
              <div
                style={{
                  maxHeight: '160px',
                  overflowY: 'auto',
                  border: '1px solid #fde68a',
                  borderRadius: '4px',
                }}
              >
                {previewData.conflicts.map((c) => (
                  <div
                    key={c.row}
                    style={{
                      borderLeft: '3px solid #f59e0b',
                      padding: '0.5rem 0.75rem',
                      borderBottom: '1px solid #fde68a',
                      fontSize: '0.875rem',
                      color: colors.text,
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>Row {c.row}:</span> {c.reason}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginTop: '1rem' }}>
            <button type="button" style={btnSecondary} onClick={returnToUpload} disabled={submitting}>
              Upload different file
            </button>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" style={btnSecondary} onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...btnPrimary,
                  opacity: commitDisabled ? 0.4 : 1,
                  cursor: commitDisabled ? 'not-allowed' : 'pointer',
                }}
                disabled={commitDisabled}
                title={previewData.invalidRows.length > 0 ? 'Fix the errors and re-upload to enable commit' : undefined}
                onClick={() => void handleCommit()}
                data-testid="commit-button"
              >
                Commit {previewData.validRows} rows
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'committing' && previewData && (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: colors.text }}>
          <span style={spinnerStyle} />
          Importing {previewData.validRows} rows…
        </div>
      )}
    </Modal>
  )
}
